import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get } from "../../lib/request";

function userOrgRoleRequestKey(organizationId, userId) {
    if (organizationId == null || userId == null) return "";
    return `${String(organizationId)}:${String(userId)}`;
}

/**
 * Fetch all organizations for a user
 */
export const fetchOrganizationsRole = createAsyncThunk(
    "organizationRole/fetchOrganizationsRole",
    async ({ userId }, { rejectWithValue, dispatch, getState }) => {
        try {
            const uid = userId != null ? String(userId).trim() : "";
            const orgsState = getState().organizations ?? {};
            const reusedOrgs =
                orgsState.organizationsListStatus === "succeeded" &&
                String(orgsState.organizationsFetchedUserId ?? "") === uid &&
                Array.isArray(orgsState.organizations)
                    ? orgsState.organizations
                    : null;
            const response = reusedOrgs ?? (await get(`organizations/users/${uid}`));
            const orgs = Array.isArray(response) ? response : [];
            if (orgs.length > 0 && orgs[0]?.id != null) {
                const key = userOrgRoleRequestKey(orgs[0].id, uid);
                const roleState = getState().organizationRole ?? {};
                const roleDone =
                    roleState.userOrgRoleStatus === "succeeded" ||
                    roleState.userOrgRoleStatus === "failed";
                const shouldFetchRole =
                    roleState.userOrgRoleStatus !== "loading" &&
                    !(roleState.userOrgRoleKey === key && roleDone);
                if (shouldFetchRole) {
                    dispatch(
                        fetchUserOrgRole({
                            organizationId: orgs[0].id,
                            userId: uid,
                        }),
                    );
                }
            }
            return { userId: uid, organizations: response };
        } catch (error) {
            return rejectWithValue(error?.message || "Failed to fetch organizations");
        }
    },
    {
        condition: ({ userId }, { getState }) => {
            const uid = userId != null ? String(userId).trim() : "";
            if (!uid) return false;
            const roleState = getState().organizationRole ?? {};
            if (roleState.organizationsStatus === "loading") return false;
            if (
                roleState.organizationsStatus === "succeeded" &&
                roleState.organizationsFetchedUserId === uid
            ) {
                return false;
            }
            return true;
        },
    },
);

/**
 * Fetch user's role inside a specific organization
 */
export const fetchUserOrgRole = createAsyncThunk(
    "organizationRole/fetchUserOrgRole",
    async ({ organizationId, userId }, { rejectWithValue }) => {
        try {
            const response = await get(`organizations/${organizationId}/users/${userId}`);
            return response;
        } catch (error) {
            return rejectWithValue(error?.message || "Failed to fetch organization role");
        }
    },
    {
        condition: ({ organizationId, userId }, { getState }) => {
            const key = userOrgRoleRequestKey(organizationId, userId);
            if (!key) return false;
            const { userOrgRoleStatus, userOrgRoleKey } = getState().organizationRole ?? {};
            if (userOrgRoleStatus === "loading") return false;
            if (
                userOrgRoleKey === key &&
                (userOrgRoleStatus === "succeeded" || userOrgRoleStatus === "failed")
            ) {
                return false;
            }
            return true;
        },
    },
);

const initialState = {
    organizations: [],
    userOrgRole: null,
    organizationsStatus: "idle",
    userOrgRoleStatus: "idle",
    organizationsFetchedUserId: null,
    userOrgRoleKey: null,
    loading: false,
    error: null,
};

const organizationRoleSlice = createSlice({
    name: "organizationRole",
    initialState,
    reducers: {
        resetOrganizationRole: () => initialState,
    },
    extraReducers: (builder) => {
        builder

            /** Fetch organizations */
            .addCase(fetchOrganizationsRole.pending, (state, action) => {
                state.loading = true;
                state.organizationsStatus = "loading";
                state.error = null;
                const uid = action.meta.arg?.userId;
                if (uid != null && String(uid).trim() !== "") {
                    state.organizationsFetchedUserId = String(uid).trim();
                }
            })
            .addCase(fetchOrganizationsRole.fulfilled, (state, action) => {
                state.loading = false;
                state.organizationsStatus = "succeeded";
                const payload = action.payload;
                const orgs =
                    payload && typeof payload === "object" && "organizations" in payload
                        ? payload.organizations
                        : payload;
                state.organizations = Array.isArray(orgs) ? orgs : [];
                state.organizationsFetchedUserId =
                    payload?.userId != null ? String(payload.userId) : state.organizationsFetchedUserId;
                const orgList = state.organizations;
                if (orgList.length === 0) {
                    state.userOrgRole = null;
                    state.userOrgRoleStatus = "succeeded";
                    state.userOrgRoleKey = null;
                }
            })
            .addCase(fetchOrganizationsRole.rejected, (state, action) => {
                state.loading = false;
                state.organizationsStatus = "failed";
                state.error = action.payload;
            })

            /** Fetch user role in organization */
            .addCase(fetchUserOrgRole.pending, (state, action) => {
                state.loading = true;
                state.userOrgRoleStatus = "loading";
                state.error = null;
                const { organizationId, userId } = action.meta.arg ?? {};
                state.userOrgRoleKey = userOrgRoleRequestKey(organizationId, userId);
            })
            .addCase(fetchUserOrgRole.fulfilled, (state, action) => {
                state.loading = false;
                state.userOrgRoleStatus = "succeeded";
                state.userOrgRole = Array.isArray(action.payload) ? action.payload[0] : action.payload;
            })
            .addCase(fetchUserOrgRole.rejected, (state, action) => {
                state.loading = false;
                state.userOrgRoleStatus = "failed";
                state.error = action.payload;
            });
    },
});

export const { resetOrganizationRole } = organizationRoleSlice.actions;
export default organizationRoleSlice.reducer;
